import { SyntheticEvent } from 'react';
import { Button, Input, Grid, FormHelperText } from '@mui/material';
import TextFieldWrapper from '../../../../common/TextFieldWrapper/TextFieldWrapper';
import { getFileNameFromPath, getUniqueUploadedFiles, countCsvFilesLines } from '../fileUploadHelpers';
import { uploadToStorage } from '../services/uploadActions';

const FileInput = ({
    placeholder,
    files,
    textInputFiles,
    cachedFiles,
    usePublicDomain,
    filePath,
    maxFiles,
    errorText,
    hiddenFileInput,
    setNotificationState,
    prepareValidFiles,
    updateProgressInfo,
    bucket,
    acl,
    setProgress,
    setFile,
    uploadCallback,
    allFiles,
    validateCsvFileLines = undefined,
    beforeUpload,
}) => {
    const filesLength = files.length || cachedFiles.length;

    const getTextInputValue = () => {
        const items = textInputFiles?.length ? textInputFiles : cachedFiles
        return items
            .map((item: any) => {
                const itemName = item.name || item.fileName
                return getFileNameFromPath(usePublicDomain, itemName, filePath)
            })
            .join(',');
    };

    const handleClick = (_: SyntheticEvent) => {
        hiddenFileInput.current?.click();
        if (setNotificationState) {
            setNotificationState({
                open: false,
                title: '',
                content: '',
            });
        }
    };

    const canUploadFiles = async (filesToUpload: File[]) => {
        try {
            if (beforeUpload) {
                return await beforeUpload(filesToUpload);
            }

            if (validateCsvFileLines && (await countCsvFilesLines(filesToUpload)) > validateCsvFileLines) {
                const confirmed = window.confirm("The number of winning moments exceeds the available vouchers. Do you want to continue?");
                return confirmed;
            }

            return true;
        } catch(e: any) {
            if (setNotificationState) {
                setNotificationState({
                    open: true,
                    title: 'Upload validation failed',
                    content: e?.message || 'The selected files could not be validated. Please try again.',
                    level: 'error',
                });
            }

            return false;
        }
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const uniqueUploadedFiles = getUniqueUploadedFiles(e, files, maxFiles, setNotificationState, allFiles)
        if (uniqueUploadedFiles) {
            const validFiles = await prepareValidFiles(uniqueUploadedFiles);
            if (!validFiles.length) {
                e.target.value = '';
                return;
            }

            const uploadConfirmed = await canUploadFiles(validFiles);
            if (!uploadConfirmed) {
                // reset input so the same file can trigger again
                e.target.value = '';
                return;
            }

            const fileKeys = await uploadToStorage(validFiles,
                updateProgressInfo,
                bucket,
                acl,
                filePath,
                usePublicDomain,
                setProgress,
                setFile);

            if (uploadCallback && validFiles.length > 0) {
                uploadCallback(fileKeys.map((key: string) => key.split('/').at(-1)));
            }

            setFile(Array.from(new Set([...files, ...validFiles])));
        }
    };

    return (
        <Grid container direction='row' className={`file-input-wrapper ${filesLength < maxFiles && 'file-input-wrapper-gap'}`}>
        <Grid item style={{ flex: 1, margin: 0 }}>
            <TextFieldWrapper
                className='file-input-field'
                placeholder={placeholder}
                disabled
                value={getTextInputValue()}
            />
            {errorText && <FormHelperText error={true}>
                    {errorText}
            </FormHelperText>}
        </Grid>
        <Grid item>
            {filesLength < maxFiles && (
                <Button className='file-button' variant='contained' color='primary' onClick={handleClick}>
                    Choose File
                </Button>
            )}
        </Grid>

        <Grid item style={{ display: 'none' }}>
            <Input
                inputRef={hiddenFileInput}
                type='file'
                inputProps={{ multiple: true, 'data-testid': 'fileInput' }}
                onChange={handleUpload}
                style={{ display: 'none' }}
            />
        </Grid>
    </Grid>
    )
}

export { FileInput };
